import { action, makeObservable, observable } from "mobx";
import { useCallback, useState } from "react";
import { Platform } from "react-native";
import type { MapData } from "../LocationModel";

export type LocationWithMapData = {
  planUid: string;
  moduleUid: string;
  dataUid?: string;
  label: string;
  mapData: MapData;
};

type LatLng = {
  lat: number;
  lng: number;
};

const PIN_CONFIG = {
  background: "#ffd84e",
  borderColor: "#ccc",
  glyphColor: "#000",
};

export class MapHandler {
  markers: google.maps.marker.AdvancedMarkerElement[] = [];
  items: LocationWithMapData[] = observable.array([], { deep: false });
  @observable selectionMode = false;
  @observable mapSelectionMarker:
    | google.maps.marker.AdvancedMarkerElement
    | undefined;

  disposers: (() => void)[] = [];

  constructor(
    public map: google.maps.Map,
    public onMarkerClick?: (item: LocationWithMapData) => void,
    public onMapClick?: (
      map: MapHandler,
      latLng: google.maps.LatLngLiteral,
    ) => void,
  ) {
    makeObservable(this);

    const clickListner = map.addListener(
      "click",
      async (event: google.maps.MapMouseEvent) => {
        const { AdvancedMarkerElement, PinElement } =
          (await google.maps.importLibrary(
            "marker",
          )) as google.maps.MarkerLibrary;

        if (this.selectionMode) {
          if (this.mapSelectionMarker) {
            this.mapSelectionMarker.position = event.latLng;
          } else {
            const pin = new PinElement(PIN_CONFIG);

            this.mapSelectionMarker = new AdvancedMarkerElement({
              map: this.map,
              position: event.latLng,
              content: pin.element,
            });
          }
        }
      },
    );

    map.addListener("click", (e: google.maps.MapMouseEvent) => {
      if (e.latLng) {
        this.onMapClick?.(this, e.latLng?.toJSON());
      }
    });

    this.disposers.push(clickListner.remove);
  }

  @action setSelectionMode(centerSelectionMode: boolean, _latLng?: LatLng) {
    this.selectionMode = centerSelectionMode;

    if (centerSelectionMode) {
      this.removeMarkers();
    } else {
      if (this.mapSelectionMarker) {
        this.mapSelectionMarker.map = null;
        this.mapSelectionMarker = undefined;
      }

      this.renderMarkers(false);
    }
  }

  setItems(items: LocationWithMapData[]) {
    const lengthChanged = this.items.length !== items.length;
    this.items = items;

    this.renderMarkers(lengthChanged);
  }

  removeMarkers() {
    this.markers.forEach((marker) => {
      marker.map = null;
    });
  }

  async renderMarkers(fitToMarkers = true) {
    const { AdvancedMarkerElement, PinElement } =
      (await google.maps.importLibrary("marker")) as google.maps.MarkerLibrary;

    this.removeMarkers();

    const markers = this.items.map((item) => {
      const latLng = item.mapData;

      const pin = new PinElement(PIN_CONFIG);

      const marker = new AdvancedMarkerElement({
        map: this.map,
        position: latLng,
        title: item.label,
        content: pin.element,
      });

      if (this.onMarkerClick) {
        const onClick = this.onMarkerClick;
        marker.addListener("click", () => {
          onClick(item);
        });
      }

      return marker;
    });

    this.markers = markers;

    if (fitToMarkers) {
      this.fitToMarkers();
    }
  }

  async fitToMarkers() {
    const map = this.map;
    if (this.markers.length) {
      const { LatLngBounds } = (await google.maps.importLibrary(
        "core",
      )) as google.maps.CoreLibrary;
      const bounds = new LatLngBounds();

      this.markers.forEach((marker) => {
        if (marker.position) {
          bounds.extend(marker.position);
        }
      });

      map.fitBounds(bounds);
      if ((map.getZoom() ?? 0) > 11) {
        map.setZoom(9);
      }
    } else {
      map.setCenter({
        lat: 54.526,
        lng: 15.2551,
      });
      map.setZoom(2);
    }
  }
}

export const useMap = (
  onMarkerClick?: (item: LocationWithMapData) => void,
  onMapClick?: (map: MapHandler, latLng: google.maps.LatLngLiteral) => void,
) => {
  if (Platform.OS !== "web") {
    return { map: undefined, mapRef: () => {} };
  }

  // biome-ignore lint/correctness/useHookAtTopLevel: Cant' change during render
  const [map, setMap] = useState<MapHandler | undefined>();

  return {
    map,
    // biome-ignore lint/correctness/useHookAtTopLevel: Cant' change during render
    mapRef: useCallback(
      (mapDiv: HTMLDivElement) => {
        (async () => {
          if (!mapDiv) {
            return;
          }

          // Request needed libraries.

          const { Map: GoogleMap } = (await google.maps.importLibrary(
            "maps",
          )) as google.maps.MapsLibrary;

          const _map = new MapHandler(
            new GoogleMap(mapDiv, {
              mapId: "LocationMap",
              streetViewControl: false,
              mapTypeControl: false,
              controlSize: 24,
            }),
            onMarkerClick,
            onMapClick,
          );

          setMap(_map);
        })();
      },
      [onMarkerClick, onMapClick],
    ),
  };
};
